import { useStore } from '@nanostores/react';
import { atom, map } from 'nanostores';

/**
 * Event status type definition (matching your events table)
 */
export type EventStatus = 'PENDING' | 'PROCESSED' | 'FAILED';
export type EventErrorState = 'ENQUEUE_ERROR' | 'UPLOAD_ERROR' | 'PROCESSING_ERROR' | 'UNKNOWN_ERROR';

export interface EventErrorDetails {
  message: string;
  code?: string;
  retryable: boolean;
  errorType: EventErrorState;
  metadata?: Record<string, any>;
}

/**
 * Event state interface
 */
export interface EventState {
  status: EventStatus;
  eventState?: EventErrorState;
  projectId: string;
  chatId: string;
  userId: string;
  timestamp: string;
  error?: EventErrorDetails;
}

/**
 * SSE Connection state
 */
export interface ConnectionState {
  isConnected: boolean;
  isConnecting: boolean;
  error: string | null;
  reconnectAttempts: number;
  /**
   * True when the server has explicitly closed the stream
   * via a "server-closed" event.
   */
  serverClosed?: boolean;
  /**
   * Optional reason for the server-initiated close.
   */
  closeReason?: string | null;
}

/**
 * Global map store for all event statuses
 * Key: eventId, Value: EventState
 */
export const eventStatusStore = map<Record<string, EventState>>({});

/**
 * Connection state store
 */
export const connectionStateStore = atom<ConnectionState>({
  isConnected: false,
  isConnecting: false,
  error: null,
  reconnectAttempts: 0,
  serverClosed: false,
  closeReason: null,
});

/**
 * Set event status
 * Note: IndexedDB synchronization is handled separately by useEventStatusSync hook
 * to avoid circular dependencies
 */
export function setEventStatus(eventId: string, state: EventState) {
  console.log('[eventStatus] setEventStatus called with eventId', eventId, 'and state', state);

  // Update the store immediately
  const current = eventStatusStore.get();
  eventStatusStore.set({
    ...current,
    [eventId]: state,
  });
}

/**
 * Get event status
 */
export function getEventStatus(eventId: string): EventState | undefined {
  return eventStatusStore.get()[eventId];
}

/**
 * Clear event status
 */
export function clearEventStatus(eventId: string) {
  console.log('[eventStatus] clearEventStatus called with eventId', eventId);
  const current = eventStatusStore.get();
  const { [eventId]: removed, ...rest } = current;
  eventStatusStore.set(rest);
}

/**
 * Clear all event statuses
 */
export function clearAllEventStatuses() {
  console.log('[eventStatus] clearAllEventStatuses called');
  eventStatusStore.set({});
}

/**
 * Set connection state
 */
export function setConnectionState(state: Partial<ConnectionState>) {
  const current = connectionStateStore.get();
  connectionStateStore.set({
    ...current,
    ...state,
  });
}

/**
 * Set event error
 */
export function setEventError(eventId: string, error: EventErrorDetails) {
  const current = eventStatusStore.get();
  const existingEvent = current[eventId];

  eventStatusStore.set({
    ...current,
    [eventId]: {
      ...existingEvent,
      status: 'FAILED',
      eventState: error.errorType,
      error,
      timestamp: new Date().toISOString(),
    } as EventState,
  });
}

/**
 * Get events with errors
 */
export function getFailedEvents(): Array<{ eventId: string } & EventState> {
  const events = useStore(eventStatusStore);

  return Object.entries(events)
    .map(([eventId, event]) => ({
      eventId,
      ...(event ?? {}),
    }))
    .filter((e) => e.status === 'FAILED' && e.error);
}

/**
 * Check if there are any failed events
 */
export function hasFailedEvents(): boolean {
  return getFailedEvents().length > 0;
}
